import { useEffect, useRef, useState } from "react";

// Vantage's own feed on TradingView, so prices match what the EAs trade on (other feeds
// differ by a few points). Anything not listed here can still be found with the chart's
// own symbol search.
const SYMBOLS = [
  { symbol: "VANTAGE:XAUUSD", label: "XAUUSD" },
  { symbol: "VANTAGE:XAGUSD", label: "XAGUSD" },
  { symbol: "VANTAGE:EURUSD", label: "EURUSD" },
  { symbol: "VANTAGE:GBPUSD", label: "GBPUSD" },
  { symbol: "VANTAGE:USDJPY", label: "USDJPY" },
  { symbol: "VANTAGE:NAS100", label: "NAS100" },
  { symbol: "VANTAGE:BTCUSD", label: "BTCUSD" },
];

const INTERVALS = [
  { value: "1", label: "1m" },
  { value: "5", label: "5m" },
  { value: "15", label: "15m" },
  { value: "30", label: "30m" },
  { value: "60", label: "1H" },
  { value: "240", label: "4H" },
  { value: "D", label: "1D" },
  { value: "W", label: "1W" },
];

const STORAGE_KEY = "mt5_dashboard_market_chart";

function loadSaved(): { symbol: string; interval: string } {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null");
    if (saved?.symbol && saved?.interval) return saved;
  } catch {
    // private window / blocked storage - fall through to the defaults
  }
  return { symbol: SYMBOLS[0].symbol, interval: "15" };
}

export function Market() {
  const [{ symbol, interval }, setChoice] = useState(loadSaved);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ symbol, interval }));
    } catch {
      // remembering the last chart is a convenience only
    }
  }, [symbol, interval]);

  return (
    <div className="flex h-full min-h-[520px] flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {SYMBOLS.map((s) => (
            <button
              key={s.symbol}
              onClick={() => setChoice({ symbol: s.symbol, interval })}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                symbol === s.symbol
                  ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-400"
                  : "border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-200"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex overflow-hidden rounded-lg border border-slate-700">
          {INTERVALS.map((i) => (
            <button
              key={i.value}
              onClick={() => setChoice({ symbol, interval: i.value })}
              className={`px-2.5 py-1 text-xs font-medium tabular-nums transition-colors ${
                interval === i.value
                  ? "bg-emerald-500/15 text-emerald-400"
                  : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
              }`}
            >
              {i.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-800 bg-slate-950">
        <TradingViewChart key={`${symbol}|${interval}`} symbol={symbol} interval={interval} />
      </div>
      <p className="-mt-2 text-xs text-slate-500">
        Times on the chart are UTC+8, the same as Malaysia time.{" "}
        <a
          href="https://www.tradingview.com/"
          target="_blank"
          rel="noopener nofollow"
          className="text-slate-400 underline-offset-2 hover:underline"
        >
          Charts by TradingView
        </a>
      </p>
    </div>
  );
}

/**
 * TradingView's free Advanced Chart widget. It runs entirely in the browser and pulls its
 * data from TradingView directly, so it costs this project's Worker and D1 nothing. The
 * widget reads its config from the script tag's body and can't be reconfigured once
 * loaded, which is why the parent remounts this (via key) on every symbol/interval change.
 */
function TradingViewChart({ symbol, interval }: { symbol: string; interval: string }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Built here rather than in JSX so the cleanup below can wipe everything the widget
    // injected (its iframe sits next to this div) and the next run starts from scratch.
    const target = document.createElement("div");
    target.className = "tradingview-widget-container__widget h-full w-full";
    container.appendChild(target);

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.async = true;
    script.innerHTML = JSON.stringify({
      autosize: true,
      symbol,
      interval,
      // TradingView has no Kuala Lumpur zone; Singapore is the same UTC+8 with no DST.
      timezone: "Asia/Singapore",
      theme: "dark",
      style: "1",
      locale: "en",
      backgroundColor: "#020617",
      gridColor: "rgba(30, 41, 59, 0.6)",
      allow_symbol_change: true,
      calendar: false,
      support_host: "https://www.tradingview.com",
    });
    container.appendChild(script);

    return () => {
      container.innerHTML = "";
    };
  }, [symbol, interval]);

  return (
<div className="tradingview-widget-container h-full w-full" ref={containerRef} />
  );
}
